/* @flow */

/**
 * @module PubSub
 * @exports PubSub
 */

/**
 * @callback PubSubCallback
 * @global
 * @param {string} Event name
 * @param {...any} Optional arguments
 * @returns {void}
 */

// Ref: https://github.com/MiguelSavignano/pub-sub-es6
declare type handlerObj = {
  cb: (string, ...args: Array<any>) => void, // flowlint-line unclear-type:off
  once: boolean
};

declare type arrHandlers = Array<handlerObj>;

declare type evtsObj = {
  [string]: arrHandlers
};

class PubSub {
  _events_: evtsObj;

  /**
   * @class PubSub
   * @description Simple PubSub class in ES6
   */
  constructor() {
    this._events_ = {};
  }

  /**
   *
   * @description Subscribe to event
   * @param {string} type Event name
   * @param {PubSubCallback} handler Callback fn
   * @param {boolean} [once] Get notification once/forever
   * @returns {void}
   */
  on(type: string, handler: cbFunc, once: boolean = false): PubSub {
    if (!this._events_[type]) {
      this._events_[type] = [];
    }

    const obj = {
      cb: handler,
      once
    };

    this._events_[type].push(obj);
    return this;
  }

  /**
   *
   * @description shortcut to get one time event notification
   * @param {string} type Event name
   * @param {PubSubCallback} handler Callback fn
   * @returns {void}
   */
  once(type: string, handler: cbFunc): PubSub {
    return this.on(type, handler, true);
  }

  /**
   * @description Unsubscribe from event
   * @param {string} type  Event name
   * @param {PubSubCallback} handler Callback fn
   * @returns {void}
   */
  off(type: string, handler: cbFunc | string): PubSub {
    if (!this._events_[type]) {
      return this;
    }

    if (!handler) {
      throw new ReferenceError(
        'handler not defined. if you wish to remove all handlers from the event please pass "*" as the handler'
      );
    }

    if (handler === '*') {
      delete this._events_[type];
      return this;
    }

    let handlers = this._events_[type];
    handlers = handlers.filter(h => h.cb !== handler);

    if (handlers.length < 1) {
      delete this._events_[type];
    } else {
      this._events_[type] = handlers;
    }

    return this;
  }

  // flowlint-next-line unclear-type:off
  emit(type: string, ...args: Array<any>): PubSub {
    if (!this._events_[type]) {
      return this.emit$(type, ...args);
    }

    const handlers: arrHandlers = this._events_[type];
    const onceHandled: arrHandlers = [];

    handlers.forEach(handler => {
      handler.cb.apply(this, args);
      if (handler.once) {
        onceHandled.push(handler);
      }
    });

    onceHandled.forEach(handler => this.off(type, handler.cb));

    return this.emit$(type, ...args);
  }

  // flowlint-next-line unclear-type:off
  emit$(type: string, ...args: Array<any>): PubSub {
    if (!this._events_['*']) {
      return this;
    }

    const catchAll = this._events_['*'];

    catchAll.forEach(handler => handler.cb.call(this, type, ...args));

    return this;
  }
}

const ps: PubSub = new PubSub();
export default ps;
